import { useState } from "react";
import { Scale, ShieldCheck, ArrowRight, Loader2, Mail, CheckCircle2, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { createCheckoutSession, submitLawyerLead } from "@/lib/api";
import { toast } from "sonner";

const LAWYER_HOURLY_RATE = 95;
const INTAKE_AMOUNT = 47.5;

export default function LawyerSection({ analysisId, hasIssues }) {
  const [tab, setTab] = useState("contact");
  // shared form
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  // contact-specific
  const [estimatedHours, setEstimatedHours] = useState("1");
  const [urgency, setUrgency] = useState("normaal");
  const [contactSubmitted, setContactSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const onContactSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Vul je naam en e-mail in.");
      return;
    }
    setLoading(true);
    try {
      await submitLawyerLead({
        analysis_id: analysisId,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        estimated_hours: Number(estimatedHours) || 1,
        urgency,
        notes: notes.trim() || null,
      });
      setContactSubmitted(true);
      toast.success("Bedankt! Onze jurist neemt binnen 24 uur contact op.");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Er ging iets mis";
      toast.error(`Verzenden mislukt: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  const onIntakeSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Vul je naam en e-mail in.");
      return;
    }
    setLoading(true);
    try {
      const origin = window.location.origin;
      const result = await createCheckoutSession({
        analysis_id: analysisId,
        package: "lawyer_intake",
        email: email.trim(),
        name: name.trim(),
        phone: phone.trim() || null,
        notes: notes.trim() || null,
        origin_url: origin,
      });
      if (result.url) {
        window.location.href = result.url;
      } else {
        throw new Error("Geen checkout URL ontvangen");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Er ging iets mis";
      toast.error(`Checkout mislukt: ${msg}`);
      setLoading(false);
    }
  };

  return (
    <section className="mt-16 relative overflow-hidden rounded-2xl" data-testid="lawyer-section">
      <div className="absolute inset-0 bg-slate-900" aria-hidden />
      <div
        aria-hidden
        className="absolute inset-0 opacity-25 bg-[url('https://images.pexels.com/photos/33562120/pexels-photo-33562120.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')] bg-cover bg-center mix-blend-overlay"
      />
      <div aria-hidden className="absolute inset-0 bg-gradient-to-r from-slate-900 via-slate-900/95 to-slate-900/70" />

      <div className="relative px-6 sm:px-10 lg:px-12 py-12 sm:py-16">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          {/* Left: pitch */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-400/30 text-amber-300 text-xs font-bold uppercase tracking-widest">
              <Scale className="h-3.5 w-3.5" />
              Optioneel · Jurist meekijken
            </div>
            <h2 className="mt-5 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">
              {hasIssues
                ? "Mogelijk onjuiste kosten of geschil gevonden."
                : "Wil je extra zekerheid van een jurist?"}
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed max-w-lg">
              Onze Nederlandse juristen (gespecialiseerd in consumentenrecht & energierecht) kijken jouw
              contract en analyse persoonlijk na — op uurbasis dus je betaalt alleen wat nodig is.
            </p>

            <div className="mt-6 inline-flex items-baseline gap-2 bg-white/[0.06] border border-white/10 px-5 py-3 rounded-xl">
              <span className="font-heading text-3xl font-bold text-white">€{LAWYER_HOURLY_RATE}</span>
              <span className="text-slate-300 text-sm">per uur · ex BTW</span>
            </div>

            <ul className="mt-7 space-y-2.5">
              {[
                "Reactie binnen 24 uur (urgent: binnen 4 uur)",
                "Persoonlijke review van jouw contractanalyse",
                "Concrete vervolgstappen + standpunt op papier",
                "Je betaalt alleen werkelijk gemaakte uren",
              ].map((b, i) => (
                <li key={i} className="flex items-start gap-2 text-slate-200 text-sm" data-testid={`lawyer-benefit-${i}`}>
                  <ShieldCheck className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" />
                  {b}
                </li>
              ))}
            </ul>
          </div>

          {/* Right: form with tabs */}
          <div className="bg-white rounded-2xl p-5 sm:p-6 shadow-2xl shadow-blue-900/40">
            {contactSubmitted ? (
              <ContactSuccess email={email} />
            ) : (
              <>
                <Tabs value={tab} onValueChange={setTab} data-testid="lawyer-tabs">
                  <TabsList className="grid grid-cols-2 w-full">
                    <TabsTrigger value="contact" data-testid="tab-contact">
                      Gratis aanvraag
                    </TabsTrigger>
                    <TabsTrigger value="intake" data-testid="tab-intake">
                      30-min intake (€{INTAKE_AMOUNT.toString().replace(".", ",")})
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="contact" className="mt-5">
                    <h3 className="font-heading text-lg font-semibold text-slate-900">
                      Vraag een jurist aan — gratis intake
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      We bellen of mailen binnen 24 uur. Geen kosten tot je akkoord gaat.
                    </p>
                    <form onSubmit={onContactSubmit} className="mt-4 space-y-3" data-testid="lawyer-contact-form">
                      <SharedFields
                        name={name} setName={setName}
                        email={email} setEmail={setEmail}
                        phone={phone} setPhone={setPhone}
                      />
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="lw-hours" className="text-xs font-semibold text-slate-900 mb-1 block">
                            Verwacht aantal uren
                          </Label>
                          <select
                            id="lw-hours"
                            value={estimatedHours}
                            onChange={(e) => setEstimatedHours(e.target.value)}
                            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            data-testid="lawyer-hours-select"
                          >
                            <option value="0.5">30 min (€{LAWYER_HOURLY_RATE / 2})</option>
                            <option value="1">1 uur (€{LAWYER_HOURLY_RATE})</option>
                            <option value="2">2 uur (€{LAWYER_HOURLY_RATE * 2})</option>
                            <option value="3">3+ uur</option>
                          </select>
                        </div>
                        <div>
                          <Label htmlFor="lw-urgency" className="text-xs font-semibold text-slate-900 mb-1 block">
                            Urgentie
                          </Label>
                          <select
                            id="lw-urgency"
                            value={urgency}
                            onChange={(e) => setUrgency(e.target.value)}
                            className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white text-sm text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-200"
                            data-testid="lawyer-urgency-select"
                          >
                            <option value="normaal">Normaal (24u)</option>
                            <option value="urgent">Urgent (4u, +€25)</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <Label htmlFor="lw-notes" className="text-xs font-semibold text-slate-900 mb-1 block">
                          Wat is je vraag? (optioneel)
                        </Label>
                        <Textarea
                          id="lw-notes"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Bv. ik twijfel over de terugleververgoeding..."
                          rows={3}
                          data-testid="lawyer-notes-input"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 text-white font-semibold rounded-lg px-5 py-3 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                        data-testid="lawyer-submit-contact-btn"
                      >
                        {loading ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Verzenden...</>
                        ) : (
                          <>Vraag jurist aan<ArrowRight className="h-4 w-4" /></>
                        )}
                      </button>
                      <p className="text-[11px] text-slate-500 text-center">
                        Door op verzenden te klikken sla je deze aanvraag op. Geen automatische betaling.
                      </p>
                    </form>
                  </TabsContent>

                  <TabsContent value="intake" className="mt-5">
                    <h3 className="font-heading text-lg font-semibold text-slate-900">
                      Boek 30-min intake direct
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">
                      Eenmalig €{INTAKE_AMOUNT.toString().replace(".", ",")} voor 30 min telefonisch consult.
                      Daarna factuur op uurbasis.
                    </p>
                    <form onSubmit={onIntakeSubmit} className="mt-4 space-y-3" data-testid="lawyer-intake-form">
                      <SharedFields
                        name={name} setName={setName}
                        email={email} setEmail={setEmail}
                        phone={phone} setPhone={setPhone}
                      />
                      <div>
                        <Label htmlFor="lw-intake-notes" className="text-xs font-semibold text-slate-900 mb-1 block">
                          Korte vraag voor intake (optioneel)
                        </Label>
                        <Textarea
                          id="lw-intake-notes"
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Korte beschrijving van je vraag..."
                          rows={2}
                          data-testid="lawyer-intake-notes-input"
                        />
                      </div>
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold rounded-lg px-5 py-3 transition-all hover:-translate-y-0.5 disabled:opacity-50"
                        data-testid="lawyer-submit-intake-btn"
                      >
                        {loading ? (
                          <><Loader2 className="h-4 w-4 animate-spin" /> Doorgaan naar Stripe...</>
                        ) : (
                          <>Boek intake · €{INTAKE_AMOUNT.toString().replace(".", ",")}<ArrowRight className="h-4 w-4" /></>
                        )}
                      </button>
                      <p className="text-[11px] text-slate-500 text-center">
                        Betaling via Stripe · Geen abonnement
                      </p>
                    </form>
                  </TabsContent>
                </Tabs>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function SharedFields({ name, setName, email, setEmail, phone, setPhone }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      <div>
        <Label htmlFor="lw-name" className="text-xs font-semibold text-slate-900 mb-1 block">
          Naam *
        </Label>
        <Input
          id="lw-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Jouw volledige naam"
          required
          data-testid="lawyer-name-input"
        />
      </div>
      <div>
        <Label htmlFor="lw-email" className="text-xs font-semibold text-slate-900 mb-1 block">
          E-mail *
        </Label>
        <Input
          id="lw-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="jij@voorbeeld.nl"
          required
          data-testid="lawyer-email-input"
        />
      </div>
      <div className="sm:col-span-2">
        <Label htmlFor="lw-phone" className="text-xs font-semibold text-slate-900 mb-1 block">
          Telefoon (optioneel — voor snelle terugbel)
        </Label>
        <div className="relative">
          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            id="lw-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="06-12345678"
            className="pl-9"
            data-testid="lawyer-phone-input"
          />
        </div>
      </div>
    </div>
  );
}

function ContactSuccess({ email }) {
  return (
    <div className="text-center py-6" data-testid="lawyer-contact-success">
      <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 grid place-items-center">
        <CheckCircle2 className="h-7 w-7 text-emerald-700" />
      </div>
      <h3 className="mt-4 font-heading text-xl font-bold text-slate-900">Aanvraag verzonden</h3>
      <p className="mt-2 text-slate-600 text-sm max-w-sm mx-auto">
        Onze jurist neemt binnen 24 uur contact op via <strong>{email}</strong> voor een gratis intake.
      </p>
      <div className="mt-4 inline-flex items-center gap-2 text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded-full px-3 py-1.5">
        <Mail className="h-3.5 w-3.5 text-blue-700" />
        Houd je inbox in de gaten
      </div>
    </div>
  );
}
